import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {take} from 'rxjs/operators';
import {AnliegenMap} from '../../core/model/anliegen-map';
import {DienststellenMap} from '../../core/model/dienststellen-map';
import {Dienststelle} from '../../core/model/dienststelle';
import {Subanliegen} from '../../core/model/anliegen';
import {NachrichtPayload} from '../../core/model/nachricht/nachricht';
import {vNiveauEnum} from '../../core/model/nachricht/vniveau.enum';
import {AnliegenHelperService} from '../anliegen-helper.service';
import {DienststellenAnliegenHelperService} from './dienststellen-anliegen-helper.service';
import {NachrichtStateEnum} from '../nachricht-state.enum';
import {NachrichtSuccessType} from '../nachricht-success-type.enum';
import {NachrichtFormComponent} from '../nachricht-form/nachricht-form.component';
import {ScrollIntoView} from '../../shared/directives/scroll-into-view.directive';
import {APP_ROUTES} from '../../route-constants';
import {NavigationService} from '../../shared/utils/navigation.service';
import {LoggingService} from '../../core/services/logging.service';
import {Rechte} from "../../core/model/rechte.model";
import {UserAccountService} from "../../core/services/user-account.service";

@Component({
    selector: 'kokos-nachricht-neu-vorbelegt',
    imports: [NachrichtFormComponent, ScrollIntoView],
    templateUrl: './nachricht-neu-vorbelegt.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class NachrichtNeuVorbelegtComponent implements OnInit {

    private readonly route = inject(ActivatedRoute);
    private readonly userAccountService = inject(UserAccountService);
    private readonly anliegenHelperService = inject(AnliegenHelperService);
    private readonly dienststellenAnliegenHelperService = inject(DienststellenAnliegenHelperService);
    private readonly navigationService = inject(NavigationService);
    private readonly loggingService = inject(LoggingService);

    nachrichtState: NachrichtStateEnum = NachrichtStateEnum.PREPARE;
    nachrichtPayload!: NachrichtPayload;

    anliegenName = '';
    subanliegenName = '';
    anhangErlaubt = false;

    nachrichtSuccessType?: NachrichtSuccessType;
    protected readonly NachrichtStateEnum = NachrichtStateEnum;

    ngOnInit(): void {
        const {anliegenId, dstnr, betreff, text} = this.parseQueryParams();
        this.loggingService.info(
            `Vorbelegung ${
                [
                    anliegenId && `anliegen:${anliegenId}`,
                    dstnr && `dstnr:${dstnr}`,
                    betreff && 'betreff:***',
                    text && 'text:***'
                ].filter(Boolean).join(', ')
            }`,
            new Map([['referrer', document.referrer]])
        );

        // anliegenId ist required
        if (!anliegenId) {
            this.fail('Fehlender Query-Parameter: anliegenId');
            return;
        }

        const routeData = this.readAndValidateRouteData();
        if (!routeData) return;
        const {anliegenMap, dienststellenMap} = routeData;


        // Fall 1: Anliegen vorhanden, keine dstnr -> Default-Dienststelle je Typ
        if (!dstnr) {
            this.handleDefaultDienststelleFlow(anliegenId, anliegenMap, dienststellenMap, betreff, text);
            return;
        }

        // 2) Anliegen und dstnr gegeben -> Dienststelle + Anliegen zur dstnr ermitteln
        this.dienststellenAnliegenHelperService
            .getDienststelleUndAnliegen(dstnr, dienststellenMap)
            .pipe(take(1))
            .subscribe({
                next: (result) => {
                    if (!result) {
                        this.fail(`Es konnte keine Dienststelle für dstnr "${dstnr}" gefunden.`);
                        return;
                    }

                    const {dienststelle, anliegen: anliegenList} = result;

                    const anliegenResult = this.anliegenHelperService.getAnliegenWithGroupName(anliegenId, anliegenList);
                    if (!anliegenResult) {
                        this.fail('Anliegen mit Id ' + anliegenId + ' ist kein gültiges Anliegen für dstnr ' + dstnr);
                        return;
                    }

                    const {gruppenName, subanliegen} = anliegenResult;
                    if (!subanliegen.nachrichtErstellen) {
                        this.fail('Für Anliegen ' + subanliegen.id + ' kann keine Nachricht erstellt werden');
                        return;
                    }

                    this.setFormState(gruppenName, subanliegen, dienststelle, betreff, text);
                },
                error: () => {
                    this.fail(`Es konnte keine Dienststelle für dstnr "${dstnr}" gefunden.`);
                },
            });
    }

    private parseQueryParams(): {
        anliegenId: string | null;
        dstnr: string | null;
        betreff: string | null;
        text: string | null;
    } {
        const qp = this.route.snapshot.queryParamMap;
        const anliegenId = qp.get('anliegen')?.trim() || null;
        const dstnr = qp.get('dstnr')?.trim() || null;
        const betreff = qp.get('betreff')?.trim() || null;
        const text = qp.get('text')?.replaceAll('<br/>', '\r\n').trim() || null;
        return {anliegenId, dstnr, betreff, text};
    }


    private readAndValidateRouteData(): {
        benutzerRechte: Rechte; // ggf. konkreten Typ verwenden
        anliegenMap: AnliegenMap;
        dienststellenMap: DienststellenMap;
    } | null {
        const benutzerRechte = this.route.snapshot.data['benutzerRechte'];
        if (!benutzerRechte) {
            throw new Error('Missing benutzerRechte');
        }

        if (this.userAccountService.isRestricted()) {
            setTimeout(() => {
                this.navigationService.goTo('/' + APP_ROUTES.KEIN_ONLINE_ZUGRIFF);
            }, 0);
        }

        const anliegenUndDienststellen = this.route.snapshot.data['anliegenUndDienststellen'] as
            | { dienststellenMap: DienststellenMap; anliegenMap: AnliegenMap }
            | undefined;

        if (!anliegenUndDienststellen) {
            throw new Error('Missing route data: anliegenUndDienststellen');
        }

        const {anliegenMap, dienststellenMap} = anliegenUndDienststellen;
        return {benutzerRechte, anliegenMap, dienststellenMap};
    }

    private handleDefaultDienststelleFlow(
        anliegenId: string,
        anliegenMap: AnliegenMap,
        dienststellenMap: DienststellenMap,
        betreff?: string | null,
        text?: string | null
    ): void {
        const anliegenResult = this.anliegenHelperService.getAnliegenWithTypeAndGroupName(anliegenId, anliegenMap);
        if (!anliegenResult) {
            this.fail('Anliegen mit Id ' + anliegenId + ' ist kein gültiges Subanliegen');
            return;
        }

        const {typ, gruppenName, subanliegen} = anliegenResult;

        if (!subanliegen.nachrichtErstellen) {
            this.fail('Zum Subanliegen ' + subanliegen.id + ' kann keine Nachricht erstellt werden');
            return;
        }

        const dienststelle = dienststellenMap[typ] ?? null;
        if (!dienststelle) {
            this.fail(`Keine Default-Dienststelle für Typ "${typ}" gefunden.`);
            return;
        }

        this.setFormState(gruppenName, subanliegen, dienststelle, betreff ?? undefined, text ?? undefined);
    }

    private fail(message: string): void {
        this.loggingService.warn(message, new Map([['referrer', document.referrer]]));
        queueMicrotask(() => this.navigationService.goTo(APP_ROUTES.NEU));
    }

    private setFormState(
        gruppenName: string,
        subanliegen: Subanliegen,
        dienststelle: Dienststelle,
        betreff: string | null,
        text: string | null
    ): void {
        this.anliegenName = gruppenName;
        this.subanliegenName = subanliegen.name;
        this.anhangErlaubt = !!subanliegen.anhangErlaubt;

        this.nachrichtPayload = this.buildNachrichtPayload(subanliegen, dienststelle, betreff, text);
        this.nachrichtState = NachrichtStateEnum.FORM;
    }

    private buildNachrichtPayload(
        subanliegen: Subanliegen,
        dienststelle: Dienststelle,
        betreff: string | null,
        text: string | null
    ): NachrichtPayload {
        return {
            bezug: subanliegen.bezug,
            empfaengerId: dienststelle.dienststellenNummer,
            empfaengerName: dienststelle.dienststellenName,
            betreff: betreff ?? '',
            text: text ?? '',
            vNiveau: vNiveauEnum.DEFAULT,
        };
    }
}